import { useState } from "react";
import { MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatesPicker } from "@/components/StatesPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Campus Coordinators pick the states they cover themselves; this drives what they see.
export const MyStatesButton = () => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!user || !["Campus Coordinator", "Campus Coordinator Supervisor"].includes(user.role)) return null;
  const current = user.managed_states || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { managed_states: states });
      await refresh();
      toast.success(states.length ? `You now cover ${states.join(", ")}` : "States cleared");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => { setStates(current); setOpen(true); }} data-testid="my-states-btn"
        className={`hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${current.length ? "border-zinc-200 text-zinc-600 hover:bg-zinc-50" : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}>
        <MapPin className="w-3.5 h-3.5" />
        {current.length ? (current.length > 4 ? `${current.length} states` : current.join(", ")) : "Set the states you cover"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="my-states-dialog">
          <DialogHeader>
            <DialogTitle>States I cover</DialogTitle>
            <DialogDescription>
              You'll see the schools, students and placements in these states, plus anything assigned to you by name.
              Unassigned schools in a newly added state are assigned to you automatically.
            </DialogDescription>
          </DialogHeader>
          <StatesPicker value={states} onChange={setStates} testId="my-states" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="my-states-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="my-states-save">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
